import React, { Suspense, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment, Html, Outlines, Text } from '@react-three/drei';
import { Card } from 'primereact/card';
import * as THREE from 'three';

type Zone = {
    id: string;
    englishName: string;
    turkishName: string;
};

// Zones - direct body parts (no mapping needed in 3D)
const zones: Zone[] = [
    {
        id: "neck",
        englishName: "Neck",
        turkishName: "Boyun"
    },
    {
        id: "shoulder",
        englishName: "Shoulder",
        turkishName: "Omuz"
    },
    {
        id: "elbow",
        englishName: "Elbow",
        turkishName: "Dirsek"
    },
    {
        id: "hand",
        englishName: "Hand",
        turkishName: "El"
    },
    {
        id: "back",
        englishName: "Back",
        turkishName: "Sırt"
    },
    {
        id: "spine",
        englishName: "Spine",
        turkishName: "Omurga"
    },
    {
        id: "core",
        englishName: "Core",
        turkishName: "Merkez Kas Bölgesi"
    },
    {
        id: "hip",
        englishName: "Hip",
        turkishName: "Kalça"
    },
    {
        id: "knee",
        englishName: "Knee",
        turkishName: "Diz"
    },
    {
        id: "abdomen",
        englishName: "Abdomen",
        turkishName: "Karın"
    }
];

// Helper function to identify zone based on mesh position
// Returns new zone IDs: neck, shoulderLeft/Right, elbowLeft/Right, handLeft/Right, back, spine, core, hipLeft/Right, kneeLeft/Right, abdomen
function identifyZone(mesh: THREE.Mesh, worldPosition: THREE.Vector3): string | null {
    const name = mesh.name.toLowerCase();
    const y = worldPosition.y;
    const x = worldPosition.x;
    
    // Neck - top part (y > 0.6)
    if (name.includes('head') || name.includes('neck') || name.includes('skull')) {
        return 'neck';
    }
    if (y > 0.6) {
        return 'neck';
    }
    
    // Shoulders - upper arms (y: 0.4-0.6, x outside center)
    if (name.includes('shoulder') || name.includes('upper') || name.includes('arm')) {
        return 'shoulder';
    }
    if (y > 0.4 && y < 0.6 && Math.abs(x) > 0.2) {
        return 'shoulder';
    }
    
    // Elbows - mid-upper arms (y: 0.2-0.4, x outside center)
    if (name.includes('elbow')) {
        return 'elbow';
    }
    if (y > 0.2 && y < 0.4 && Math.abs(x) > 0.25) {
        return 'elbow';
    }
    
    // Hands - lower arms (y: 0.0-0.2, x outside center)
    if (name.includes('hand') || name.includes('wrist')) {
        return 'hand';
    }
    if (y > 0.0 && y < 0.2 && Math.abs(x) > 0.25) {
        return 'hand';
    }
    
    // Back, Spine, Core - center upper/mid torso (y: 0.3-0.6, x: -0.2 to 0.2)
    if (name.includes('chest') || name.includes('sternum') || name.includes('pectoral') || name.includes('back') || name.includes('spine')) {
        if (name.includes('spine')) return 'spine';
        if (name.includes('back')) return 'back';
        if (name.includes('core')) return 'core';
        // Default chest area can be back, spine, or core - assign based on position
        if (y > 0.5) return 'spine';
        if (y > 0.4) return 'back';
        return 'core';
    }
    if (y > 0.3 && y < 0.6 && Math.abs(x) < 0.2) {
        if (y > 0.5) return 'spine';
        if (y > 0.4) return 'back';
        return 'core';
    }
    
    // Hips - upper lower torso (y: 0.0-0.2, x outside center)
    if (name.includes('hip') || name.includes('pelvis')) {
        return 'hip';
    }
    if (y > 0.0 && y < 0.2 && Math.abs(x) > 0.15 && Math.abs(x) < 0.3) {
        return 'hip';
    }
    
    // Knees - mid lower (y: -0.2 to 0.0, x outside center)
    if (name.includes('knee')) {
        return 'knee';
    }
    if (y > -0.2 && y < 0.0 && Math.abs(x) > 0.15) {
        return 'knee';
    }
    
    // Abdomen - middle crossover area (y: 0.15-0.35, x: -0.3 to 0.3)
    if (name.includes('abdomen') || name.includes('stomach') || name.includes('belly')) {
        return 'abdomen';
    }
    if (y > 0.15 && y < 0.35 && Math.abs(x) > 0.1 && Math.abs(x) < 0.3) {
        return 'abdomen';
    }
    
    return null;
}

// Component to load and display the GLB model with click detection
function Model({ 
    selectedFigures, 
    setSelectedFigures, 
    onAreaSelected, 
    step,
    controlsRef,
    t
}: { 
    selectedFigures: Zone[]; 
    setSelectedFigures: (zones: Zone[] | ((prev: Zone[]) => Zone[])) => void;
    onAreaSelected?: () => void;
    step?: number;
    controlsRef: React.MutableRefObject<any>;
    t?: (key: string) => string;
}) {
    const gltf = useGLTF('/ecorche_-_anatomy_study.glb');
    const modelReadyRef = useRef(false);
    const meshRefs = useRef<Map<THREE.Mesh, string>>(new Map());
    const occluderMeshesRef = useRef<THREE.Mesh[]>([]);
    const zoneCentersRef = useRef<Record<string, THREE.Vector3>>({});
    const groupRef = useRef<THREE.Group>(null);
    const { camera, gl, raycaster } = useThree();
    const pointer = useRef({ x: 0, y: 0 });
    const [zoomedInZones, setZoomedInZones] = useState<Set<string>>(new Set());
    const [hoveredZone, setHoveredZone] = useState<string | null>(null);
    const markerElementRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const markerWorldPositionRef = useRef(new THREE.Vector3());
    const markerDirectionRef = useRef(new THREE.Vector3());
    const markerProjectionRef = useRef(new THREE.Vector3());
    const markerOpacityCacheRef = useRef<Record<string, string>>(
        zones.reduce((acc, zone) => {
            acc[zone.id] = '1';
            return acc;
        }, {} as Record<string, string>)
    );
    const markerVisibilityInitializedRef = useRef(false);
    const visibilityDirtyRef = useRef(true);
    const visibilityZoneCursorRef = useRef(0);
    const lastCameraPositionRef = useRef(new THREE.Vector3());
    const lastCameraQuaternionRef = useRef(new THREE.Quaternion());
    
    useEffect(() => {
        if (gltf.scene && !modelReadyRef.current) {
            requestAnimationFrame(() => {
                const box = new THREE.Box3().setFromObject(gltf.scene);
                const center = box.getCenter(new THREE.Vector3());
                const size = box.getSize(new THREE.Vector3());
                const maxDim = Math.max(size.x, size.y, size.z);
                
                // Reset transformations
                gltf.scene.scale.set(1, 1, 1);
                gltf.scene.position.set(0, 0, 0);
                gltf.scene.rotation.set(0, 0, 0);
                
                // Recalculate after reset
                box.setFromObject(gltf.scene);
                const newCenter = box.getCenter(new THREE.Vector3());
                const newSize = box.getSize(new THREE.Vector3());
                const newMaxDim = Math.max(newSize.x, newSize.y, newSize.z);
                
                // Center and position
                gltf.scene.position.x = -newCenter.x;
                gltf.scene.position.y = -newCenter.y + 0.3;
                gltf.scene.position.z = -newCenter.z;
                
                // Normalize size
                const targetSize = 2.5;
                if (newMaxDim > 0) {
                    const scale = targetSize / newMaxDim;
                    gltf.scene.scale.set(scale, scale, scale);
                }
                
                // Map ALL meshes to zones based on their position
                let meshCount = 0;
                occluderMeshesRef.current = [];
                gltf.scene.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        meshCount++;
                        occluderMeshesRef.current.push(child);
                        // Get world position after transformations
                        const worldPos = new THREE.Vector3();
                        child.getWorldPosition(worldPos);
                        
                        // Use normalized position (after centering and scaling)
                        const y = worldPos.y;
                        const x = worldPos.x;
                        let zoneId: string | null = null;
                        
                        // First try name-based identification
                        zoneId = identifyZone(child, worldPos);
                        
                        // If not found by name, use precise position-based identification
                        // Match the new zone IDs with clear boundaries
                        if (!zoneId) {
                            // Neck - top part (y > 0.55)
                            if (y > 0.55) {
                                zoneId = 'neck';
                            }
                            // Shoulders - upper arms (y: 0.4-0.6, x outside center)
                            else if (y > 0.4 && y <= 0.6) {
                                if (Math.abs(x) > 0.18) zoneId = 'shoulder';
                                // Center area: back, spine, or core
                                else if (y > 0.5) zoneId = 'spine';
                                else if (y > 0.45) zoneId = 'back';
                                else zoneId = 'core';
                            }
                            // Elbows - mid-upper arms (y: 0.25-0.4, x outside center)
                            else if (y > 0.25 && y <= 0.4) {
                                if (Math.abs(x) > 0.25) zoneId = 'elbow';
                                // Center area: abdomen or core
                                else if (Math.abs(x) > 0.1) zoneId = 'abdomen';
                                else zoneId = 'core';
                            }
                            // Abdomen - middle crossover area (y: 0.15-0.35, x: -0.3 to 0.3)
                            else if (y > 0.15 && y <= 0.35 && Math.abs(x) > 0.08 && Math.abs(x) < 0.28) {
                                zoneId = 'abdomen';
                            }
                            // Hands/Hips - mid-lower (y: 0.0-0.25, x outside center)
                            else if (y > 0.0 && y <= 0.25) {
                                if (Math.abs(x) > 0.25) zoneId = 'hand';
                                else if (Math.abs(x) > 0.15) zoneId = 'hip';
                                else zoneId = 'abdomen';
                            }
                            // Knees - lower (y: -0.25 to 0.0, x outside center)
                            else if (y > -0.25 && y <= 0.0) {
                                if (Math.abs(x) > 0.15) zoneId = 'knee';
                                else zoneId = 'abdomen';
                            }
                            // Lower parts (y < -0.25) - don't assign to knee, leave as null or assign to a different zone
                            else {
                                // Don't assign lower parts to knee - they should be unassigned or assigned elsewhere
                                zoneId = null;
                            }
                        }
                        
                        // Final fallback - ensure every mesh gets a zone (shouldn't happen but safety net)
                        if (!zoneId) {
                            if (y > 0.45) zoneId = 'neck';
                            else if (y > 0.4 && Math.abs(x) < 0.2) zoneId = 'core';
                            else if (y > 0.15 && Math.abs(x) < 0.3) zoneId = 'abdomen';
                            else if (y > 0) zoneId = 'shoulder';
                            else if (y > -0.25 && Math.abs(x) > 0.15) zoneId = 'knee';
                            // Don't assign very low parts (y < -0.25) to any zone
                        }
                        
                        if (zoneId) {
                            meshRefs.current.set(child, zoneId);
                            child.userData.zoneId = zoneId;
                            child.userData.clickable = step === 1;
                        }
                    }
                });
                
                const zoneDistribution = Array.from(meshRefs.current.values()).reduce((acc, zone) => {
                    acc[zone] = (acc[zone] || 0) + 1;
                    return acc;
                }, {} as Record<string, number>);
                
                // Calculate center positions for each zone based on actual mesh positions
                const zonePositions: Record<string, THREE.Vector3[]> = {};
                gltf.scene.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        const zoneId = meshRefs.current.get(child);
                        if (zoneId) {
                            if (!zonePositions[zoneId]) {
                                zonePositions[zoneId] = [];
                            }
                            const worldPos = new THREE.Vector3();
                            child.getWorldPosition(worldPos);
                            zonePositions[zoneId].push(worldPos);
                        }
                    }
                });
                
                // Calculate average center for each zone
                Object.entries(zonePositions).forEach(([zoneId, positions]) => {
                    if (positions.length > 0) {
                        // For neck specifically, use topmost and frontmost position ON the surface
                        if (zoneId === 'neck') {
                            // Find the topmost and frontmost position
                            let maxY = -Infinity;
                            let maxZ = -Infinity;
                            let bestPos = positions[0].clone();
                            
                            // First pass: find highest Y positions
                            const topPositions = positions.filter(pos => {
                                const currentMaxY = Math.max(...positions.map(p => p.y));
                                return pos.y >= currentMaxY * 0.95; // Top 5% of head
                            });
                            
                            // Second pass: from top positions, find frontmost (highest Z)
                            if (topPositions.length > 0) {
                                topPositions.forEach(pos => {
                                    if (pos.z > maxZ) {
                                        maxZ = pos.z;
                                        bestPos = pos.clone();
                                    }
                                });
                            } else {
                                // Fallback: just use highest Y
                                positions.forEach(pos => {
                                    if (pos.y > maxY) {
                                        maxY = pos.y;
                                        bestPos = pos.clone();
                                    }
                                });
                            }
                            
                            // Position marker ON the neck/head area - keep it higher
                            bestPos.y -= 0.15; // Small adjustment to neck/head junction area (was -0.4, now -0.15 to make it higher)
                            bestPos.z += 0.05; // Small forward offset to be visible on surface
                            zoneCentersRef.current[zoneId] = bestPos;
                        } else {
                            // For other zones, use average center
                            const center = new THREE.Vector3();
                            positions.forEach(pos => center.add(pos));
                            center.divideScalar(positions.length);
                            center.z += 0.12;
                            zoneCentersRef.current[zoneId] = center;
                        }
                    }
                });
                
                console.log(`✅ Mapped ${meshRefs.current.size} meshes out of ${meshCount} total meshes`);
                console.log('Zone distribution:', zoneDistribution);
                
                // Verify zones are properly distributed
                const expectedZones = ['neck', 'shoulderLeft', 'shoulderRight', 'elbowLeft', 'elbowRight', 'handLeft', 'handRight', 'back', 'spine', 'core', 'hipLeft', 'hipRight', 'kneeLeft', 'kneeRight', 'abdomen'];
                expectedZones.forEach(zoneId => {
                    const count = zoneDistribution[zoneId] || 0;
                    if (count === 0) {
                        console.warn(`⚠️ Zone "${zoneId}" has no meshes assigned!`);
                    } else {
                        console.log(`  ${zoneId}: ${count} meshes`);
                    }
                });
                
                modelReadyRef.current = true;
            });
        }
    }, [gltf.scene]);
    
    // Handle click events - TEST: Only head is selectable
    const handlePointerDown = useCallback((event: any) => {
        console.log('🔵 handlePointerDown CALLED!', {
            step,
            object: event.object?.name || 'unnamed',
            userData: event.object?.userData
        });
        
        if (step !== 1) {
            console.log('❌ Step is not 1, ignoring');
            return;
        }
        
        const mesh = event.object as THREE.Mesh;
        
        if (!mesh || !(mesh instanceof THREE.Mesh)) {
            console.log('❌ Not a mesh');
            return;
        }
        
        // Get zoneId
        let zoneId = mesh.userData.zoneId;
        
        if (!zoneId) {
            for (const [storedMesh, id] of meshRefs.current.entries()) {
                if (storedMesh === mesh || storedMesh.uuid === mesh.uuid) {
                    zoneId = id;
                    break;
                }
            }
        }
        
        // If still no zoneId, try to identify by current position
        if (!zoneId) {
            const worldPos = new THREE.Vector3();
            mesh.getWorldPosition(worldPos);
            zoneId = identifyZone(mesh, worldPos);
            if (zoneId) {
                meshRefs.current.set(mesh, zoneId);
                mesh.userData.zoneId = zoneId;
            }
        }
        
        console.log('🔍 Found zoneId:', zoneId, 'for mesh:', mesh.name || 'unnamed');
        
        // Find the zone and toggle selection
        const zone = zones.find(z => z.id === zoneId);
        if (zone) {
            setSelectedFigures(prev => {
                const isSelected = prev.some(fig => fig.id === zoneId);
                if (isSelected) {
                    return prev.filter(fig => fig.id !== zoneId);
                } else {
                    return [...prev, zone];
                }
            });
            onAreaSelected?.();
        }
    }, [step, setSelectedFigures, onAreaSelected, camera, gl]);
    
    // Re-map meshes when step changes - but don't cause re-render
    useEffect(() => {
        if (gltf.scene && modelReadyRef.current) {
            // Use requestAnimationFrame to prevent blocking render
            requestAnimationFrame(() => {
                gltf.scene.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        child.userData.clickable = step === 1;
                    }
                });
            });
        }
    }, [step, gltf.scene]);
    
    // Update mesh materials based on selection - preserve highlighting across step changes
    useFrame(() => {
        if (!gltf.scene) return;
        
        gltf.scene.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                const zoneId = meshRefs.current.get(child) || child.userData.zoneId;
                if (zoneId) {
                    // Check if this zone is selected (works for all steps)
                    const isSelected = selectedFigures.some(fig => fig.id === zoneId);
                    
                    // Update material - highlight selected zones
                    if (child.material instanceof THREE.MeshStandardMaterial) {
                        if (isSelected) {
                            // Bright blue highlight for selected zones
                            child.material.emissive.setHex(0x0066ff);
                            child.material.emissiveIntensity = 0.8;
                            child.material.opacity = 1;
                            child.material.transparent = false;
                        } else {
                            // Reset non-selected zones
                            child.material.emissive.setHex(0x000000);
                            child.material.emissiveIntensity = 0;
                            child.material.opacity = 1;
                            child.material.transparent = false;
                        }
                    }
                    
                    // Make sure mesh is interactive only in step 1
                    if (step === 1) {
                        child.userData.zoneId = zoneId;
                    }
                }
            }
        });

        const hasMovedEnough = camera.position.distanceToSquared(lastCameraPositionRef.current) > 0.0002;
        const hasRotatedEnough = 1 - Math.abs(camera.quaternion.dot(lastCameraQuaternionRef.current)) > 0.00005;
        if (hasMovedEnough || hasRotatedEnough) {
            visibilityDirtyRef.current = true;
            lastCameraPositionRef.current.copy(camera.position);
            lastCameraQuaternionRef.current.copy(camera.quaternion);
        }

        if (!markerVisibilityInitializedRef.current && zones.length > 0) {
            const allMarkersMounted = zones.every(zone => !!markerElementRefs.current[zone.id]);
            if (allMarkersMounted) {
                zones.forEach((zone) => {
                    markerWorldPositionRef.current.set(...getZonePosition(zone.id));
                    markerProjectionRef.current.copy(markerWorldPositionRef.current).project(camera);

                    const inFrustum =
                        markerProjectionRef.current.z > -1 &&
                        markerProjectionRef.current.z < 1 &&
                        Math.abs(markerProjectionRef.current.x) <= 1 &&
                        Math.abs(markerProjectionRef.current.y) <= 1;

                    let isOccluded = false;
                    if (inFrustum) {
                        markerDirectionRef.current.subVectors(markerWorldPositionRef.current, camera.position);
                        const markerDistance = markerDirectionRef.current.length();

                        if (markerDistance > 0.001) {
                            markerDirectionRef.current.normalize();
                            raycaster.near = 0;
                            raycaster.far = Math.max(markerDistance - 0.02, 0.001);
                            raycaster.set(camera.position, markerDirectionRef.current);
                            const intersections = raycaster.intersectObjects(occluderMeshesRef.current, false);
                            if (intersections.length > 0) {
                                isOccluded = intersections[0].distance < markerDistance - 0.04;
                            }
                        }
                    }

                    markerOpacityCacheRef.current[zone.id] = (!inFrustum || isOccluded) ? '0.35' : '1';
                });

                markerVisibilityInitializedRef.current = true;
                visibilityDirtyRef.current = false;
                visibilityZoneCursorRef.current = 0;
                lastCameraPositionRef.current.copy(camera.position);
                lastCameraQuaternionRef.current.copy(camera.quaternion);
            }
        }

        if (markerVisibilityInitializedRef.current && visibilityDirtyRef.current && zones.length > 0) {
            const checksPerFrame = 1;
            for (let i = 0; i < checksPerFrame; i++) {
                const zone = zones[visibilityZoneCursorRef.current];
                const markerElement = markerElementRefs.current[zone.id];
                if (markerElement) {
                    markerWorldPositionRef.current.set(...getZonePosition(zone.id));
                    markerProjectionRef.current.copy(markerWorldPositionRef.current).project(camera);

                    const inFrustum =
                        markerProjectionRef.current.z > -1 &&
                        markerProjectionRef.current.z < 1 &&
                        Math.abs(markerProjectionRef.current.x) <= 1 &&
                        Math.abs(markerProjectionRef.current.y) <= 1;

                    let isOccluded = false;
                    if (inFrustum) {
                        markerDirectionRef.current.subVectors(markerWorldPositionRef.current, camera.position);
                        const markerDistance = markerDirectionRef.current.length();

                        if (markerDistance > 0.001) {
                            markerDirectionRef.current.normalize();
                            raycaster.near = 0;
                            raycaster.far = Math.max(markerDistance - 0.02, 0.001);
                            raycaster.set(camera.position, markerDirectionRef.current);
                            const intersections = raycaster.intersectObjects(occluderMeshesRef.current, false);
                            if (intersections.length > 0) {
                                isOccluded = intersections[0].distance < markerDistance - 0.04;
                            }
                        }
                    }

                    markerOpacityCacheRef.current[zone.id] = (!inFrustum || isOccluded) ? '0.35' : '1';
                }

                visibilityZoneCursorRef.current = (visibilityZoneCursorRef.current + 1) % zones.length;
                if (visibilityZoneCursorRef.current === 0) {
                    visibilityDirtyRef.current = false;
                }
            }
        }

        zones.forEach((zone) => {
            const markerElement = markerElementRefs.current[zone.id];
            if (!markerElement) return;

            if (!markerVisibilityInitializedRef.current) {
                if (markerElement.style.visibility !== 'hidden') {
                    markerElement.style.visibility = 'hidden';
                }
                if (markerElement.style.opacity !== '0') {
                    markerElement.style.opacity = '0';
                }
                return;
            }

            if (markerElement.style.visibility !== 'visible') {
                markerElement.style.visibility = 'visible';
            }
            const targetOpacity = markerOpacityCacheRef.current[zone.id] ?? '1';
            if (markerElement.style.opacity !== targetOpacity) {
                markerElement.style.opacity = targetOpacity;
            }
        });
    });
    
    // Fallback positions if zone centers aren't calculated yet (based on model scale and position)
    // Model is scaled to 2.5 units max, centered at origin, Y offset +0.3
    const fallbackPositions: Record<string, [number, number, number]> = {
        neck: [0, 0.85, -0.05],              // Neck area (moved up from 0.85 to 0.95)
        shoulder: [-0.3, 0.73, -0.1],          // Shoulder
        elbow: [0.40, 0.3, -0.2],            // Elbow
        hand: [-0.45, -0.1, 0],              // Hand
        back: [0.1, 0.65, -0.2],              // Back
        spine: [0, 0.35, -0.17],               // Spine
        core: [0, 0.6, 0.1],               // Core - chest area
        hip: [-0.2, 0.1, -0.04],              // Hip
        knee: [0.15, -0.6, 0],            // Knee
        abdomen: [0, 0.23, 0.1]              // Abdomen
    };
    
    // Get zone position - use calculated center if available, otherwise fallback
    const getZonePosition = (zoneId: string): [number, number, number] => {
        // For knee, always use fallback position to ensure it's positioned correctly
        if (zoneId === 'knee') {
            return fallbackPositions[zoneId] || [0, 0, 0];
        }
        const center = zoneCentersRef.current[zoneId];
        if (center) {
            return [center.x, center.y, center.z];
        }
        return fallbackPositions[zoneId] || [0, 0, 0];
    };
    
    // Zone labels/numbers - assign numbers to zones
    const zoneLabels: Record<string, string> = {
        neck: '1',
        shoulder: '2',
        elbow: '3',
        hand: '4',
        core: '5',
        abdomen: '6',
        back: '7',
        spine: '8',
        hip: '9',
        knee: '10'
    };
    
    // Ensure neck is always first in the filter
    const sortedZones = zones.sort((a, b) => {
        if (a.id === 'neck') return -1;
        if (b.id === 'neck') return 1;
        return 0;
    });
    
    const handleMarkerClick = useCallback((zoneId: string) => {
        if (step !== 1) return;
        
        const zone = zones.find(z => z.id === zoneId);
        if (zone) {
            console.log('✅ Marker clicked:', zoneId, zone.englishName);
            
            // Get the position for this zone - use getZonePosition to ensure consistency
            const positionArray = getZonePosition(zoneId);
            const position = new THREE.Vector3(...positionArray);
            
            // Animate camera to focus on the clicked zone
            if (zoneId === 'neck' || zoneId === 'shoulder' || zoneId === 'elbow' || zoneId === 'hand' || zoneId === 'core' || zoneId === 'abdomen' || zoneId === 'back' || zoneId === 'spine' || zoneId === 'hip' || zoneId === 'knee') {
                // Determine camera position based on zone
                let targetPosition: THREE.Vector3;
                if (zoneId === 'neck') {
                    // Neck - approach from left side
                    targetPosition = new THREE.Vector3(
                        position.x - 0.5, // Left side of neck
                        position.y + 0.3, // Slightly above the neck
                        position.z + 0.4  // Slightly forward
                    );
                } else if (zoneId === 'shoulder') {
                    // Shoulder - approach from left side
                    targetPosition = new THREE.Vector3(
                        position.x - 1, // Left side of shoulder
                        position.y + 0.2, // Slightly above the shoulder
                        position.z + 0.3  // Slightly forward
                    );
                } else if (zoneId === 'elbow') {
                    // Elbow - approach from right side
                    targetPosition = new THREE.Vector3(
                        position.x + 0.8, // Right side of elbow
                        position.y + 0.2, // Slightly above the elbow
                        position.z + 0.3  // Slightly forward
                    );
                } else if (zoneId === 'hand') {
                    // Hand - approach from left side
                    targetPosition = new THREE.Vector3(
                        position.x - 0.9, // Left side of hand
                        position.y + 0.1, // Slightly above the hand
                        position.z + 0.3  // Slightly forward
                    );
                } else if (zoneId === 'core') {
                    // Core - approach from front (chest area)
                    targetPosition = new THREE.Vector3(
                        position.x, // Center
                        position.y, // Same height as core
                        position.z + 1  // Front view of chest - camera positioned in front looking back
                    );
                } else if (zoneId === 'abdomen') {
                    // Abdomen - approach from front
                    targetPosition = new THREE.Vector3(
                        position.x, // Center
                        position.y, // Same height as abdomen
                        position.z + 1  // Front view of abdomen
                    );
                } else if (zoneId === 'back') {
                    // Back - approach from behind to show full back view
                    targetPosition = new THREE.Vector3(
                        position.x, // Center horizontally
                        position.y + 0.1, // Slightly above the back
                        position.z -1  // Behind the figure to view the back surface
                    );
                } else if (zoneId === 'spine') {
                    // Spine - approach from behind to show full back view
                    targetPosition = new THREE.Vector3(
                        position.x, // Center horizontally
                        position.y + 0.1, // Slightly above the spine
                        position.z -1  // Behind the figure to view the spine
                    );
                } else if (zoneId === 'hip') {
                    // Hip - approach from left side
                    targetPosition = new THREE.Vector3(
                        position.x - 0.8, // Left side of hip
                        position.y + 0.2, // Slightly above the hip
                        position.z + 0.4  // Slightly forward
                    );
                } else if (zoneId === 'knee') {
                    // Knee - approach from right side (exactly same as elbow)
                    targetPosition = new THREE.Vector3(
                        position.x + 0.8, // Right side of knee
                        position.y + 0.2, // Slightly above the knee
                        position.z + 0.3  // Slightly forward
                    );
                }
                
                // Show tooltip when zooming in for this specific zone (add to set, don't replace)
                setZoomedInZones(prev => new Set(prev).add(zoneId));
                
                // Smooth camera animation
                const startPos = camera.position.clone();
                const startTarget = controlsRef.current?.target.clone() || new THREE.Vector3(0, 0, 0);
                const endTarget = position.clone();
                
                let progress = 0;
                const duration = 1000; // 1 second animation
                const startTime = Date.now();
                
                const animate = () => {
                    const elapsed = Date.now() - startTime;
                    progress = Math.min(elapsed / duration, 1);
                    
                    // Easing function (ease-in-out)
                    const eased = progress < 0.5 
                        ? 2 * progress * progress 
                        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
                    
                    camera.position.lerpVectors(startPos, targetPosition, eased);
                    
                    // Update OrbitControls target
                    if (controlsRef.current) {
                        controlsRef.current.target.lerpVectors(startTarget, endTarget, eased);
                        controlsRef.current.update();
                    }
                    
                    if (progress < 1) {
                        requestAnimationFrame(animate);
                    } else {
                        // Hide tooltip after animation completes (optional - or keep it visible)
                        // setTimeout(() => setIsZoomedIn(false), 3000);
                    }
                };
                
                animate();
            }
            
            setSelectedFigures(prev => {
                const isSelected = prev.some(fig => fig.id === zone.id);
                if (isSelected) {
                    // Hide tooltip when deselecting this specific zone (remove from set)
                    setZoomedInZones(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(zoneId);
                        return newSet;
                    });
                    return prev.filter(fig => fig.id !== zone.id);
                } else {
                    return [...prev, zone];
                }
            });
            onAreaSelected?.();
        }
    }, [step, setSelectedFigures, onAreaSelected, camera, gl]);
    
    return (
        <group ref={groupRef}>
            <primitive object={gltf.scene} />
            
            {/* Clickable 3D spheres with HTML labels - Show markers in step 1, or show selected markers in other steps */}
            {((step === 1) || (step !== 1 && selectedFigures.some(fig => fig.id === 'neck' || fig.id === 'shoulder' || fig.id === 'elbow' || fig.id === 'hand' || fig.id === 'core' || fig.id === 'abdomen' || fig.id === 'back' || fig.id === 'spine' || fig.id === 'hip' || fig.id === 'knee'))) && zones.filter(zone => zone.id === 'neck' || zone.id === 'shoulder' || zone.id === 'elbow' || zone.id === 'hand' || zone.id === 'core' || zone.id === 'abdomen' || zone.id === 'back' || zone.id === 'spine' || zone.id === 'hip' || zone.id === 'knee').map((zone) => {
                const zoneId = zone.id;
                const position = getZonePosition(zoneId);
                const isSelected = selectedFigures.some(fig => fig.id === zoneId);
                console.log('🎯 Rendering head marker at position:', position, 'isSelected:', isSelected);
                
                return (
                    <group key={zoneId} position={position}>
                        {/* Visible marker is now the click target so hit area matches what user sees. */}
                        <Html
                            center
                            style={{
                                pointerEvents: step === 1 ? 'auto' : 'none',
                                userSelect: 'none'
                            }}
                        >
                            <div
                                ref={(element) => {
                                    markerElementRefs.current[zoneId] = element;
                                    if (element) {
                                        element.style.visibility = markerVisibilityInitializedRef.current ? 'visible' : 'hidden';
                                        element.style.opacity = markerVisibilityInitializedRef.current
                                            ? (markerOpacityCacheRef.current[zoneId] ?? '1')
                                            : '0';
                                    }
                                }}
                                onClick={(e) => {
                                    if (step !== 1) return;
                                    e.stopPropagation();
                                    handleMarkerClick(zoneId);
                                }}
                                onMouseEnter={() => {
                                    if (step !== 1) return;
                                    setHoveredZone(zoneId);
                                    gl.domElement.style.cursor = 'pointer';
                                }}
                                onMouseLeave={() => {
                                    if (step !== 1) return;
                                    setHoveredZone(null);
                                    gl.domElement.style.cursor = 'default';
                                }}
                                style={{
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: '50%',
                                    backgroundColor: isSelected ? '#2D3967' : (hoveredZone === zoneId ? '#4A6FE8' : '#6383F4'),
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '11px',
                                    lineHeight: 1,
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    border: '0.75px solid white',
                                    boxShadow: hoveredZone === zoneId ? '0 4px 12px rgba(99, 131, 244, 0.6)' : '0 2px 6px rgba(0,0,0,0.3)',
                                    pointerEvents: step === 1 ? 'auto' : 'none',
                                    transform: hoveredZone === zoneId ? 'scale(1.15)' : 'scale(1)',
                                    transition: 'all 0.2s ease-in-out',
                                    cursor: step === 1 ? 'pointer' : 'default',
                                    visibility: markerVisibilityInitializedRef.current ? 'visible' : 'hidden',
                                    opacity: markerVisibilityInitializedRef.current
                                        ? (markerOpacityCacheRef.current[zoneId] ?? '1')
                                        : 0
                                }}
                            >
                                {zoneLabels[zoneId]}
                            </div>
                        </Html>
                        
                        {/* Tooltip showing when zoomed in and selected - unique to each button */}
                        {zoomedInZones.has(zoneId) && isSelected && (zoneId === 'neck' || zoneId === 'shoulder' || zoneId === 'elbow' || zoneId === 'hand' || zoneId === 'core' || zoneId === 'abdomen' || zoneId === 'back' || zoneId === 'spine' || zoneId === 'hip' || zoneId === 'knee') && (
                            <Html
                                center
                                style={{
                                    pointerEvents: 'none',
                                    userSelect: 'none',
                                    transform: 'translateY(-30px)'
                                }}
                            >
                                <div
                                    style={{
                                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                                        color: 'white',
                                        padding: '6px 10px',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap',
                                        pointerEvents: 'none',
                                        boxShadow: '0 3px 10px rgba(0,0,0,0.5)',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {t ? (zoneId === 'neck' ? t('neck') : zoneId === 'shoulder' ? t('shoulder') : zoneId === 'elbow' ? t('elbow') : zoneId === 'hand' ? t('hand') : zoneId === 'core' ? t('core') : zoneId === 'abdomen' ? t('abdomen') : zoneId === 'back' ? t('back') : zoneId === 'spine' ? t('spine') : zoneId === 'hip' ? t('hip') : t('knee')) : (zoneId === 'neck' ? 'Neck' : zoneId === 'shoulder' ? 'Shoulder' : zoneId === 'elbow' ? 'Elbow' : zoneId === 'hand' ? 'Hand' : zoneId === 'core' ? 'Core' : zoneId === 'abdomen' ? 'Abdomen' : zoneId === 'back' ? 'Back' : zoneId === 'spine' ? 'Spine' : zoneId === 'hip' ? 'Hip' : 'Knee')}
                                </div>
                            </Html>
                        )}
                    </group>
                );
            })}
        </group>
    );
}

// Component to handle cursor changes for OrbitControls
function CursorController({ controlsRef, step }: { controlsRef: React.MutableRefObject<any>; step: number }) {
    const { gl } = useThree();
    
    useEffect(() => {
        const controls = controlsRef.current;
        if (!controls) return;

        // Keep a "hand" cursor on inactive steps so the whole card feels clickable.
        if (step !== 1) {
            gl.domElement.style.cursor = 'pointer';
            return () => {
                gl.domElement.style.cursor = 'default';
            };
        }
        
        const handleStart = () => {
            gl.domElement.style.cursor = 'grabbing';
        };
        
        const handleEnd = () => {
            gl.domElement.style.cursor = 'default';
        };
        
        // OrbitControls from drei uses these event names
        controls.addEventListener('start', handleStart);
        controls.addEventListener('end', handleEnd);
        
        return () => {
            if (controls) {
                controls.removeEventListener('start', handleStart);
                controls.removeEventListener('end', handleEnd);
            }
            gl.domElement.style.cursor = 'default';
        };
    }, [gl, controlsRef, step]);
    
    return null;
}

// Component to handle camera reset functionality
const CameraResetController = forwardRef<{ resetCamera: () => void }, { controlsRef: React.MutableRefObject<any> }>(
    ({ controlsRef }, ref) => {
        const { camera } = useThree();
        
        const resetCamera = useCallback(() => {
            // Reset camera to initial position
            const initialPosition = new THREE.Vector3(0, 0.2, 3);
            const initialTarget = new THREE.Vector3(0, 0, 0);
            
            // Animate camera reset smoothly
            const startPos = camera.position.clone();
            const startTarget = controlsRef.current?.target.clone() || new THREE.Vector3(0, 0, 0);
            const duration = 500; // 0.5 seconds
            let progress = 0;
            const startTime = Date.now();
            
            const animate = () => {
                const elapsed = Date.now() - startTime;
                progress = Math.min(elapsed / duration, 1);
                
                // Easing function (ease-in-out)
                const eased = progress < 0.5 
                    ? 2 * progress * progress 
                    : 1 - Math.pow(-2 * progress + 2, 2) / 2;
                
                camera.position.lerpVectors(startPos, initialPosition, eased);
                
                // Update OrbitControls target
                if (controlsRef.current) {
                    controlsRef.current.target.lerpVectors(startTarget, initialTarget, eased);
                    controlsRef.current.update();
                }
                
                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };
            
            animate();
        }, [camera, controlsRef]);
        
        useImperativeHandle(ref, () => ({
            resetCamera
        }));
        
        return null;
    }
);

CameraResetController.displayName = 'CameraResetController';

export interface AnatomyModel3DRef {
    handleReset: () => void;
}

interface AnatomyModel3DProps {
    wrapperCard?: boolean;
    height?: string;
    selectedFigures?: Zone[];
    setSelectedFigures?: (zones: Zone[] | ((prev: Zone[]) => Zone[])) => void;
    onAreaSelected?: () => void;
    step?: number;
    t?: (key: string) => string;
}

const AnatomyModel3D = forwardRef<AnatomyModel3DRef, AnatomyModel3DProps>(({ 
    wrapperCard = false, 
    height = '100%',
    selectedFigures = [],
    setSelectedFigures = () => {},
    onAreaSelected,
    step = 1,
    t
}, ref) => {
    const controlsRef = useRef<any>(null);
    const cameraResetRef = useRef<{ resetCamera: () => void }>(null);
    
    const handleReset = useCallback(() => {
        setSelectedFigures([]);
        // Reset camera and zoom to initial position
        cameraResetRef.current?.resetCamera();
    }, [setSelectedFigures]);
    
    useImperativeHandle(ref, () => ({
        handleReset
    }));
    
    const canvasRef = useRef<HTMLDivElement>(null);
    const sizeLockedRef = useRef(false);
    const [lockedSize, setLockedSize] = useState<{ width: number; height: number } | null>(null);
    
    // Lock Canvas size after first render to prevent resizing on step changes
    useEffect(() => {
        if (canvasRef.current && !sizeLockedRef.current) {
            const updateSize = () => {
                const rect = canvasRef.current?.getBoundingClientRect();
                if (rect && rect.width > 0 && rect.height > 0) {
                    setLockedSize({ width: rect.width, height: rect.height });
                    sizeLockedRef.current = true;
                }
            };
            
            // Wait for next frame to ensure container is rendered
            requestAnimationFrame(() => {
                requestAnimationFrame(updateSize);
            });
        }
    }, []);
    
    // Don't memoize - let React handle it naturally, but use stable key
    const canvasContent = (
        <div 
            ref={canvasRef}
            style={{ 
                width: lockedSize ? `${lockedSize.width}px` : '100%', 
                height: lockedSize ? `${lockedSize.height}px` : height, 
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                willChange: 'auto'
            }}
        >
            <Canvas
                key="anatomy-canvas-stable" // Stable key to prevent remounting
                shadows
                style={{ width: '100%', height: '100%', display: 'block' }}
                camera={{ position: [0, 0.2, 3], fov: 50 }}
                gl={{ 
                    preserveDrawingBuffer: true, // Preserve buffer to prevent flicker
                    powerPreference: "high-performance",
                    antialias: true
                }}
                dpr={[1, 1.5]} // Limit pixel ratio to prevent excessive rendering
                frameloop="always" // Keep rendering to prevent flicker
            >
                <Suspense fallback={
                    <Html center>
                        <div style={{ color: '#666', fontSize: '16px' }}>Loading 3D Model...</div>
                    </Html>
                }>
                    {/* Lighting */}
                    <ambientLight intensity={0.8} />
                    <directionalLight 
                        position={[5, 5, 5]} 
                        intensity={0} 
                        castShadow
                    />
                    <directionalLight 
                        position={[-5, 5, -5]} 
                        intensity={0}
                    />
                    <pointLight position={[0, 10, 0]} intensity={0.5} />
                    
                    {/* 3D Model */}
                    <Model 
                        selectedFigures={selectedFigures}
                        setSelectedFigures={setSelectedFigures}
                        onAreaSelected={onAreaSelected}
                        step={step}
                        controlsRef={controlsRef}
                        t={t}
                    />
                    
                    {/* Environment for better lighting */}
                    <Environment preset="sunset" />
                    
                    {/* Cursor controller for rotation */}
                    <CursorController controlsRef={controlsRef} step={step} />
                    
                    {/* Camera reset controller */}
                    <CameraResetController ref={cameraResetRef} controlsRef={controlsRef} />
                    
                    {/* Controls for rotating/zooming */}
                    <OrbitControls 
                        ref={controlsRef}
                        enablePan={true}
                        enableZoom={true}
                        enableRotate={true}
                        minDistance={0.3}
                        maxDistance={4}
                        autoRotate={false}
                        target={[0, 0, 0]}
                        enableDamping={false}
                        makeDefault
                    />
                </Suspense>
            </Canvas>
        </div>
    );

    if (wrapperCard) {
        return (
            <Card style={{ borderRadius: "24px", overflow: 'hidden' }}>
                {canvasContent}
            </Card>
        );
    }

    return canvasContent;
});

AnatomyModel3D.displayName = 'AnatomyModel3D';

// Preload the model
useGLTF.preload('/ecorche_-_anatomy_study.glb');

export default AnatomyModel3D;

